import { useEffect, useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import DataTable from '../../components/ui/DataTable.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { toastError } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import {
  AUDIT_ACTIONS,
  AUDIT_MODULES,
  getAuditActionLabel,
  getAuditModuleLabel,
} from '../../constants/auditLog.js';
import { ADMIN_ICON_BTN_CLASS, ADMIN_INPUT_CLASS } from '../../constants/adminUi.js';

const inputClass = ADMIN_INPUT_CLASS;

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PayloadBlock({ payload }) {
  if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
    return <p className="text-slate-400">Sin datos adicionales.</p>;
  }
  return (
    <pre className="max-h-64 overflow-auto rounded-lg bg-white/5 p-4 text-xs text-slate-300">
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
}

export default function AuditoriaAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [filterModulo, setFilterModulo] = useState('');
  const [filterAccion, setFilterAccion] = useState('');
  const [filterUsuario, setFilterUsuario] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [modulos, setModulos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/admin/audit-log', {
        params: {
          page,
          per_page: perPage,
          search: debouncedSearch || undefined,
          modulo: filterModulo || undefined,
          accion: filterAccion || undefined,
          usuario_id: filterUsuario || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
        if (data.data.modulos) setModulos(data.data.modulos);
        if (data.data.usuarios) setUsuarios(data.data.usuarios);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch, filterModulo, filterAccion, filterUsuario, dateFrom, dateTo]);

  async function openDetail(row) {
    setDetailOpen(true);
    setDetailRow(row);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/v1/admin/audit-log/${row.id}`);
      if (data.success) setDetailRow(data.data);
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setDetailLoading(false);
    }
  }

  const columns = useMemo(
    () => [
      {
        key: 'fecha_creacion',
        label: 'Fecha',
        render: (row) => formatDateTime(row.fecha_creacion),
      },
      {
        key: 'usuario_email',
        label: 'Usuario',
        render: (row) => row.usuario_email || 'Sistema',
      },
      {
        key: 'modulo',
        label: 'Módulo',
        render: (row) => getAuditModuleLabel(row.modulo),
      },
      {
        key: 'accion',
        label: 'Acción',
        render: (row) => getAuditActionLabel(row.accion),
      },
      {
        key: 'resumen',
        label: 'Resumen',
        className: 'max-w-xs truncate',
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <button
            type="button"
            className={ADMIN_ICON_BTN_CLASS}
            title="Ver detalle"
            onClick={() => openDetail(row)}
          >
            <Eye className="h-4 w-4" />
          </button>
        ),
      },
    ],
    []
  );

  const moduloOptions = useMemo(() => {
    const fromApi = modulos.map((m) => ({ value: m, label: getAuditModuleLabel(m) }));
    const known = Object.entries(AUDIT_MODULES).map(([value, label]) => ({ value, label }));
    const seen = new Set();
    return [...fromApi, ...known].filter((o) => {
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    });
  }, [modulos]);

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold text-accent-blue md:text-4xl">Auditoría</h1>
        <p className="mt-2 max-w-2xl text-slate-600">
          Historial de acciones en el panel administrativo: creaciones, ediciones, eliminaciones e inicios de sesión.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <select
          className={`${inputClass} max-w-xs`}
          value={filterModulo}
          onChange={(e) => {
            setFilterModulo(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los módulos</option>
          {moduloOptions.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          className={`${inputClass} max-w-xs`}
          value={filterAccion}
          onChange={(e) => {
            setFilterAccion(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todas las acciones</option>
          {AUDIT_ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <select
          className={`${inputClass} max-w-xs`}
          value={filterUsuario}
          onChange={(e) => {
            setFilterUsuario(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los usuarios</option>
          {usuarios.map((u) => (
            <option key={u.id} value={String(u.id)}>
              {u.email}
            </option>
          ))}
        </select>
        <input
          type="date"
          className={`${inputClass} max-w-[11rem]`}
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(1);
          }}
          aria-label="Desde"
        />
        <input
          type="date"
          className={`${inputClass} max-w-[11rem]`}
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(1);
          }}
          aria-label="Hasta"
        />
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} title="Detalle de auditoría" wide>
        {detailLoading && !detailRow?.user_agent ? (
          <p className="text-sm text-slate-400">Cargando…</p>
        ) : detailRow ? (
          <div className="space-y-4 text-sm text-slate-300">
            <p>
              <span className="font-semibold text-white">ID:</span> {detailRow.id}
            </p>
            <p>
              <span className="font-semibold text-white">Fecha:</span> {formatDateTime(detailRow.fecha_creacion)}
            </p>
            <p>
              <span className="font-semibold text-white">Usuario:</span> {detailRow.usuario_email || 'Sistema'}
              {detailRow.usuario_id ? ` (id ${detailRow.usuario_id})` : ''}
            </p>
            <p>
              <span className="font-semibold text-white">Módulo:</span> {getAuditModuleLabel(detailRow.modulo)}
            </p>
            <p>
              <span className="font-semibold text-white">Acción:</span> {getAuditActionLabel(detailRow.accion)}
            </p>
            {detailRow.registro_id ? (
              <p>
                <span className="font-semibold text-white">Registro:</span> #{detailRow.registro_id}
              </p>
            ) : null}
            <div>
              <p className="font-semibold text-white">Resumen</p>
              <p className="mt-2 rounded-lg bg-white/5 p-4">{detailRow.resumen}</p>
            </div>
            <p>
              <span className="font-semibold text-white">IP:</span> {detailRow.ip || '—'}
            </p>
            <div>
              <p className="font-semibold text-white">User-Agent</p>
              <p className="mt-2 break-all rounded-lg bg-white/5 p-4 text-xs">{detailRow.user_agent || '—'}</p>
            </div>
            <div>
              <p className="font-semibold text-white">Payload</p>
              <div className="mt-2">
                <PayloadBlock payload={detailRow.payload} />
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
